import React, { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { FirstDayOfWeek, toDay } from "../../lib/datetime";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { Icon } from "../../components";
import { useAnalytics } from "../../lib/analytics/analytics";
import { formatDate, startOfMonth } from "../../offering/date_utils";
import { isAfter, isSameDay } from "date-fns";
import {
  DateRangePicker,
  PickingRange,
} from "./date_range_picker/date_range_picker";
import { parseDay } from "@/offering/day_utils";
import { DatePickerV2 } from "./date_picker_v2";
import { useStandartPickerOnMembershipFeatureFlag } from "@/core/splitio";

interface MembershipDatePickerWrapperProps {
  value?: Date;
  testID?: string;
  numberOfMonths?: number;
  orientation?: "vertical" | "horizontal";
  isBlocked?: (date: Date) => boolean;
  firstDayOfWeek?: FirstDayOfWeek;
  onComplete: (day: string) => void;
  placeholder?: string;
  availableDates?: string[];
}

export function MembershipDatePickerWrapper(
  props: MembershipDatePickerWrapperProps
) {
  const {
    value,
    onComplete,
    placeholder,
    availableDates,
    firstDayOfWeek,
    orientation,
    isBlocked,
    numberOfMonths,
  } = props;
  const standartPickerOnMembershipFeatureFlag =
    useStandartPickerOnMembershipFeatureFlag();
  const [internalValue, setInternalValue] = useState(value);
  const [show, setShow] = useState(false);

  const internalValueAsDay = internalValue ? toDay(internalValue) : undefined;

  const handleChange = (date: Date) => setInternalValue(date);

  const analytics = useAnalytics();

  const firstAvailableDate =
    availableDates && availableDates.length
      ? new Date(availableDates[0])
      : new Date();

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const { fbt } = useI18n();

  const dateRangePickerComponent = (
    <>
      {standartPickerOnMembershipFeatureFlag ? (
        <DateRangePicker
          availableDates={availableDates?.map((day) => parseDay(day))}
          pickingRange={PickingRange.OneDate}
          onChange={({ startDate }) => {
            handleChange(startDate);
          }}
          value={{ startDate: internalValue, endDate: internalValue }}
        />
      ) : (
        <DatePickerV2
          firstDayOfWeek={firstDayOfWeek}
          onChange={handleChange}
          value={internalValue}
          isBlocked={isBlocked}
          numberOfMonths={numberOfMonths}
          orientation={orientation}
          blockNextRender={(navigationDate) =>
            isAfter(
              startOfMonth(navigationDate),
              startOfMonth(firstAvailableDate)
            )
          }
          blockPrevRender={(navigationDate) =>
            isSameDay(
              startOfMonth(navigationDate),
              startOfMonth(firstAvailableDate)
            )
          }
          availableDates={availableDates}
        />
      )}
      <div style={{ padding: 16 }}>
        <button
          disabled={!internalValue}
          className="btn btn-primary"
          onClick={() => {
            setShow(false);
            !!internalValueAsDay && onComplete(internalValueAsDay);
            analytics.event(
              "Save Date Click",
              {
                "Membership Start Date": internalValueAsDay,
                Source: "WL",
              },
              { onlyProviders: ["mixpanel", "customerio"] }
            );
          }}
        >
          {fbt("Save")}
        </button>
      </div>
    </>
  );

  return (
    <div className="checkout-box-picker-view">
      <Dropdown
        className="checkout-box-picker row"
        id={`checkout-date-picker`}
        show={show}
        onToggle={(isOpen) => setShow(isOpen)}
      >
        <Dropdown.Toggle>
          <div className="row">
            <div className="col-10 checkout-box-picker-placeholder">
              <span className="row fw-bold text-uppercase checkout-box-picker-placeholder__label">
                {placeholder ? fbt(placeholder) : fbt("Start Date")}
              </span>
              <span className="row checkout-box-picker-selected">
                {value ? (
                  toDay(value) === toDay(new Date()) ? (
                    `${formatDate(toDay(value))} (${fbt("Today")})`
                  ) : (
                    formatDate(toDay(value))
                  )
                ) : (
                  <span className="fw-lighter checkout-box-picker-selected__placeholder">
                    {fbt("Select date")}
                  </span>
                )}
              </span>
            </div>
            <div className="checkout-box-picker-icon">
              <Icon name="fi-chevron-down" size={24} />
            </div>
          </div>
        </Dropdown.Toggle>
        {standartPickerOnMembershipFeatureFlag ? (
          <Dropdown.Menu
            className="date-picker-v2 rounded"
            popperConfig={{
              modifiers: [
                {
                  name: "preventOverflow",
                  options: {
                    altAxis: true,
                    padding: { top: 80 },
                  },
                },
              ],
            }}
            style={{ minWidth: 350 }}
            data-testid="checkout-date-picker-calendar"
          >
            {dateRangePickerComponent}
          </Dropdown.Menu>
        ) : (
          <Dropdown.Menu className="date-picker-v2 rounded">
            {dateRangePickerComponent}
          </Dropdown.Menu>
        )}
      </Dropdown>
    </div>
  );
}
